import type { OTCForwardFieldsData } from '@src/types/OTC';
import { isForwardProductFields } from '@src/types/OTC/otcGuards';
import { getFieldTitle, getFieldTooltip } from '@src/utils/fieldDefinitionUtils';
import {
  appendTooltipWhenFeatureDisabled,
  PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import { otcForwardFieldDefinitions } from '@src/utils/OTC/otcForwardFieldDefinitions';
import {
  Box,
  DurationInput,
  FormGroup,
  HStack,
  NumberInput,
  OTCSettlementTypeEnum,
  SearchSelect,
  Stack,
  type Leaves,
} from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { FORWARD_PAYOFF_TYPE_OPTIONS, OTC_SETTLEMENT_TYPE_OPTIONS } from '../productDefinitions/tokens';
import type { OTCFieldsContext } from './types';
import {
  useCommonOtcDerivativeProductLayoutLogic,
  type IndexOption,
  type PayoffTypeOption,
  type UsdPeggedOption,
} from './useCommonOtcDerivativeProductLayoutLogic';

interface ForwardProductLayoutProps {
  formData: OTCForwardFieldsData;
  handleFieldChange: (field: Leaves<OTCForwardFieldsData>, value: string | boolean | null | undefined) => void;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  requiredFields?: Record<string, boolean>;
  disclosureContext: OTCFieldsContext;
  enableOTCPhysicalSettlement: boolean;
}

export const ForwardProductLayout = ({
  formData,
  disclosureContext,
  handleFieldChange,
  errors = {},
  touched = {},
  requiredFields = {},
  enableOTCPhysicalSettlement,
}: ForwardProductLayoutProps) => {
  if (!isForwardProductFields(formData)) {
    throw new Error('Form data is not a forward product');
  }

  useEffect(() => {
    if (!enableOTCPhysicalSettlement && formData.settlementType === OTCSettlementTypeEnum.Physical) {
      handleFieldChange('settlementType', OTCSettlementTypeEnum.Cash);
    }
  }, [enableOTCPhysicalSettlement, formData.settlementType, handleFieldChange]);
  const {
    usdPeggedOptions,
    usdPeggedSortFilter,
    availableIndexes,
    selectedIndex,
    payoffDependentData,
    assetSelections,
    payoffTypeSelection,
  } = useCommonOtcDerivativeProductLayoutLogic(formData, disclosureContext);

  const settlementTypeSelection = useMemo(() => {
    return OTC_SETTLEMENT_TYPE_OPTIONS.find(option => option.value === formData.settlementType);
  }, [formData.settlementType]);

  return (
    <Stack gap="spacing12" w="100%">
      <FormGroup
        label={getFieldTitle(otcForwardFieldDefinitions.payoffType)}
        tooltip={getFieldTooltip(otcForwardFieldDefinitions.payoffType)}
        required={requiredFields.payoffType}
      >
        <SearchSelect<PayoffTypeOption>
          options={FORWARD_PAYOFF_TYPE_OPTIONS}
          selection={payoffTypeSelection}
          onChange={option => handleFieldChange('payoffType', option?.value)}
          getLabel={option => option?.label}
          placeholder="Select Payoff Type"
          showClear
          data-testid="otc-payoff-type-select"
        />
      </FormGroup>
      <HStack gap="spacing12" mb="spacing8">
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcForwardFieldDefinitions.quoteAsset)}
            tooltip={getFieldTooltip(otcForwardFieldDefinitions.quoteAsset)}
            required={requiredFields.quoteAsset}
            error={touched.quoteAsset ? errors.quoteAsset : ''}
          >
            <SearchSelect<UsdPeggedOption>
              options={usdPeggedOptions}
              selection={assetSelections.quoteAssetSelection}
              onChange={option => handleFieldChange('quoteAsset', option?.value)}
              getLabel={option => String(option?.label || '')}
              sortFilterOverride={usdPeggedSortFilter}
              placeholder="Select Quote Asset"
              showClear
              touched={!!touched.quoteAsset}
              invalid={!!errors.quoteAsset}
              data-testid="otc-quote-asset-select"
            />
          </FormGroup>
        </Box>
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcForwardFieldDefinitions.deliveryAsset)}
            tooltip={getFieldTooltip(otcForwardFieldDefinitions.deliveryAsset)}
            required={requiredFields.deliveryAsset}
            error={touched.deliveryAsset ? errors.deliveryAsset : ''}
          >
            <SearchSelect<UsdPeggedOption>
              options={usdPeggedOptions}
              disabled={formData.payoffType === 'Inverse'}
              selection={assetSelections.deliveryAssetSelection}
              onChange={option => handleFieldChange('deliveryAsset', option?.value)}
              getLabel={option => String(option?.label || '')}
              placeholder={payoffDependentData.deliveryAssetPlaceholder}
              sortFilterOverride={usdPeggedSortFilter}
              showClear={formData.payoffType !== 'Inverse'}
              touched={!!touched.deliveryAsset}
              invalid={!!errors.deliveryAsset}
              data-testid="otc-delivery-asset-select"
            />
          </FormGroup>
        </Box>
      </HStack>

      <HStack gap="spacing12" mb="spacing8">
        <FormGroup
          flex="1"
          label={getFieldTitle(otcForwardFieldDefinitions.unitNotional)}
          tooltip={getFieldTooltip(otcForwardFieldDefinitions.unitNotional)}
          required={requiredFields.unitNotionalAmount}
          error={touched.unitNotionalAmount ? errors.unitNotionalAmount : ''}
        >
          <NumberInput
            id="unit-notional-amount"
            autoComplete="off"
            min="0"
            value={formData.unitNotionalAmount?.toString() || ''}
            onChange={value => handleFieldChange('unitNotionalAmount', value)}
            suffix={payoffDependentData.unitNotionalCurrency}
            data-testid="manual-otc-trade-form-unit-notional"
            touched={!!touched.unitNotionalAmount}
            invalid={!!errors.unitNotionalAmount}
          />
        </FormGroup>
      </HStack>

      <FormGroup
        label={getFieldTitle(otcForwardFieldDefinitions.tenor)}
        tooltip={getFieldTooltip(otcForwardFieldDefinitions.tenor)}
        required={requiredFields.tenor}
        mb="spacing8"
      >
        <DurationInput
          initialValue={formData.tenor}
          onChange={value => handleFieldChange('tenor', value)}
          dataTestId="otc-term-input"
          placeholder="Enter custom tenor"
        />
      </FormGroup>

      <HStack gap="spacing12" alignItems="center">
        <FormGroup
          flex="1"
          label={getFieldTitle(otcForwardFieldDefinitions.index)}
          tooltip={getFieldTooltip(otcForwardFieldDefinitions.index)}
          required={requiredFields.index}
          error={touched.index ? errors.index : ''}
          mb="spacing8"
        >
          <SearchSelect<IndexOption>
            options={availableIndexes}
            selection={selectedIndex}
            onChange={option => handleFieldChange('index', option?.value || '')}
            getLabel={option => option?.label}
            getDescription={option => option?.description}
            placeholder="Select Index"
            showClear={false}
            disabled={!disclosureContext.rowData.baseAsset || !formData.quoteAsset}
            touched={!!touched.index}
            invalid={!!errors.index}
            data-testid="manual-otc-trade-form-index"
          />
        </FormGroup>
      </HStack>

      <FormGroup
        label={getFieldTitle(otcForwardFieldDefinitions.settlementType)}
        tooltip={appendTooltipWhenFeatureDisabled(
          getFieldTooltip(otcForwardFieldDefinitions.settlementType),
          enableOTCPhysicalSettlement,
          PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING
        )}
        required={requiredFields.settlementType}
      >
        <SearchSelect<(typeof OTC_SETTLEMENT_TYPE_OPTIONS)[number]>
          options={OTC_SETTLEMENT_TYPE_OPTIONS}
          selection={settlementTypeSelection}
          onChange={option => handleFieldChange('settlementType', option?.value)}
          getLabel={option => option?.label}
          placeholder="Cash"
          data-testid="otc-settlement-type-select"
          disabled={!enableOTCPhysicalSettlement}
        />
      </FormGroup>
    </Stack>
  );
};
